import { useCallback, useMemo, useState, type ComponentProps } from "react";
import {
  Box,
  EditableValueCell,
  Text,
  type EditableValueCellAction,
  type EditableValueCellEditor,
  type Omittable,
} from "@salestrak/ui";
import { AddressInlineInput } from "#app/components/custom-fields/address-inline-input";
import {
  buildCopyValueAction,
  getFieldTypeDescriptor,
} from "#app/components/fields/field-type-descriptors";
import { NONE_OPTION_LABEL } from "#app/lib/custom-field-utils";
import {
  PROPERTY_CONTROL_HEIGHT,
  PROPERTY_VALUE_REST_INSET_CLASS,
} from "#app/components/fields/property-value-inset";
import { useOpenAddressOnMap } from "#app/lib/hooks/use-open-address-on-map";

/**
 * The built-in Address PROPERTY cell, sibling to {@link TextValueCell}: the
 * same `EditableValueCell` composition a custom field of type `address` gets —
 * same activation, same touch reveal, same hover value-actions row (map, copy,
 * clear), same outside-click dismissal — reading its action set and noun from
 * the `address` descriptor so a table showing both an address property column
 * and an address custom-field column cannot present two editing UXs. Only the
 * commit differs: the property writes through the issue's address mutation,
 * not the values map.
 *
 * Editing types IN the cell: the Places search input mounts where the value
 * rests and only the suggestion list floats below — on a table row and on a
 * property row alike, so the project drawer's Address edits where its text
 * properties edit, not in a popover of its own.
 *
 * The cell owns its activation/highlight state — table cells have no
 * dialog-style container tracking rows.
 */

export type AddressValueCellProps = Omittable<{
  wholeAddress?: string | null;
  /** Coordinates the host already holds (an issue's address entity). "Open
   *  in Map" uses them directly instead of geocoding the text. */
  latLng?: { lat: number; lng: number } | null;
  addressId?: string | null;
  onSelect: (wholeAddress: string) => void;
  onClear: () => void;
  /** Replaces the default "Open in Map" navigation for a host that has to
   *  do more than jump (the project drawer collapses out of fullscreen and
   *  opens the issue's own pin). */
  onOpenInMap?: () => void;
  /** Editing started or ended — for a host whose neighbouring rows derive
   *  from this value and should hold still while it is being changed. */
  onActiveChange?: (active: boolean) => void;
  placeholder?: string;
  /** Text tone for an empty value. */
  emptyTextVariant?: ComponentProps<typeof Text>["variant"];
  /** View/comment access tiers: display, copy and map only. */
  readOnly?: boolean;
  /** Cell height in px. Defaults to the 32px property control rung on the property
   *  surface; a table cell fills its host (the row minus the 2px ring inset). */
  height?: number;
  /** Table hosts pass false — a row clips the end bleed square. */
  bleedEnd?: boolean;
  /** Where the text sits. `cell` is the table grammar: the shared `md` cell
   *  inset. `property` is the property panel's: the 7px inset the sibling
   *  selector Buttons put their labels at. `card` is the board card's token:
   *  the cell fills the token's value segment at an 8px inset, paints no
   *  hover or ring of its own (the token washes and turns its edge brand),
   *  keeps only the copy action and reads a clipped value out through the
   *  text's overflow tooltip. None leads the value with a glyph — the text
   *  is the whole value. */
  inset?: "cell" | "property" | "card";
}>;

export function AddressValueCell({
  wholeAddress,
  latLng,
  addressId,
  onSelect,
  onClear,
  onOpenInMap,
  onActiveChange,
  placeholder,
  emptyTextVariant = "faint",
  readOnly = false,
  height: heightProp,
  bleedEnd = true,
  inset = "cell",
}: AddressValueCellProps) {
  const isProperty = inset === "property";
  const isCard = inset === "card";
  const height = heightProp ?? (isProperty ? PROPERTY_CONTROL_HEIGHT : undefined);
  const [isActive, setIsActiveState] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const setIsActive = useCallback(
    (next: boolean) => {
      setIsActiveState(next);
      onActiveChange?.(next);
    },
    [onActiveChange],
  );

  const descriptor = getFieldTypeDescriptor("address");
  const hasValue = !!wholeAddress;
  const openAddressOnMap = useOpenAddressOnMap("table_cell");

  // Commits arrive from the clear action (`overwrite(null)`); selections go
  // straight through `onSelect` in the editor below.
  const handleCommit = useCallback(
    (next: unknown) => {
      if (typeof next === "string" && next.trim()) onSelect(next);
      else onClear();
    },
    [onClear, onSelect],
  );

  const actions = useMemo<EditableValueCellAction[]>(() => {
    if (!hasValue) return [];
    const noun = descriptor.actionNoun;
    const list: EditableValueCellAction[] = [];
    // Derived from the descriptor's action set, in its order, so this cell and
    // an address custom-field cell always offer the same actions. `open` and
    // `fullscreen` have no property-address behavior and are skipped.
    for (const action of descriptor.actions) {
      switch (action) {
        case "map":
          list.push({
            key: "map",
            icon: "map-pin",
            label: `Open ${noun} in Map`,
            tooltip: "Open in Map",
            onSelect: () => {
              if (onOpenInMap) {
                onOpenInMap();
                return;
              }
              if (!wholeAddress) return;
              void openAddressOnMap({ wholeAddress, latLng, addressId });
            },
          });
          break;
        case "copy":
          list.push(buildCopyValueAction(noun, () => wholeAddress ?? null));
          break;
        case "clear":
          list.push({
            key: "clear",
            icon: "x",
            label: `Clear ${noun}`,
            tooltip: "Clear",
            onSelect: (cell) => cell.overwrite(null),
          });
          break;
      }
    }
    // The card keeps map + copy — a glance surface, where every reserved
    // action slot is width the value loses, and jumping to the pin is the
    // one thing a board reader does with an address. No clear: destructive
    // next to a drag handle is not card material.
    if (isCard) return list.filter((a) => a.key === "map" || a.key === "copy");
    return readOnly ? list.filter((a) => a.key !== "clear") : list;
  }, [
    addressId,
    descriptor,
    hasValue,
    isCard,
    latLng,
    onOpenInMap,
    openAddressOnMap,
    readOnly,
    wholeAddress,
  ]);

  // Cell surface: inset-free, the cell wears `inset="md"` in both states.
  // Property surface: the display and the editor each carry the property
  // inset, so the text holds still when editing starts.
  const display = (
    <Box
      fill="flex"
      direction="row"
      align="center"
      gap="xs"
      className={isProperty ? PROPERTY_VALUE_REST_INSET_CLASS : undefined}
    >
      <Box fill="flex" minWidth={0}>
        <Text
          size="sm"
          variant={hasValue ? undefined : emptyTextVariant}
          truncate={1}
          // The card grammar's clip rule: a clipped address reads out in
          // full on hover.
          overflowTooltip={isCard}
        >
          {wholeAddress || placeholder || NONE_OPTION_LABEL}
        </Text>
      </Box>
    </Box>
  );

  // Same editor as the custom-field `address` cell: the search input mounts
  // inside the editing cell (the cell paints the ring/fill), suggestions
  // float below it.
  const editor: EditableValueCellEditor = {
    kind: "inline",
    render: ({ deactivate }) => (
      <AddressInlineInput
        wholeAddress={wholeAddress}
        placeholder={placeholder}
        // The card's inset is worn by the cell around the editor, like the
        // table's.
        inset={isProperty ? "property" : "cell"}
        // 26px inside the token's hairlines = the `sm` rung on desktop.
        size={isCard ? "sm" : undefined}
        onSelect={(address) => {
          onSelect(address);
          deactivate();
        }}
        onDeactivate={deactivate}
      />
    ),
  };

  return (
    // Table surface: a 2px vertical inset floats the hover/edit ring inside
    // the row — flush against the row hairlines it merges with them and
    // disappears (the same reason DataTableCellTrigger wears my-0.5); the
    // cell fills the rest, so it is the row minus that inset at every
    // density. Property rows are borderless at rest and fill their 32px row
    // exactly.
    <Box
      fill="flex"
      minWidth={0}
      direction="row"
      align="stretch"
      paddingY={isProperty || isCard ? undefined : "2xs"}
    >
      <EditableValueCell
        value={wholeAddress ?? null}
        display={display}
        hasValue={hasValue}
        editor={readOnly ? undefined : editor}
        triggerLabel={`Edit ${descriptor.actionNoun}`}
        height={height}
        inset={isProperty ? undefined : isCard ? "sm" : "md"}
        bleedEnd={bleedEnd}
        // Property rows hover as a full-width ghost fill, like the selector
        // triggers beside them; table cells keep the outline ring; the card
        // token washes and rings itself and hugs its value, so the actions
        // ride in flow after the text, collapsed at rest (`token`).
        restHover={isProperty ? "fill" : isCard ? "token" : "outline"}
        outline={!isCard}
        readOnly={readOnly}
        actions={actions}
        onCommit={handleCommit}
        isActive={isActive}
        onActivate={() => setIsActive(true)}
        onDeactivate={() => setIsActive(false)}
        highlighted={overlayOpen || isActive}
        onOverlayChange={setOverlayOpen}
      />
    </Box>
  );
}
